import type { ListCellContext, ListColumn } from 'ziti-console-lib';
import { ListActionButton, ListActionButtonSpec, ListActionsCellComponent } from './list-actions-cell.component';
import { HoverActionsCellComponent, HoverCellBadge } from './hover-actions-cell.component';

/** The key of the last column of a list, which holds the row buttons that are not in the ⋮ menu. */
export const LIST_ACTIONS_COLUMN = 'actions';

/** A row the list table draws a ⋮ menu for. */
export type ActionListRow<T> = T & { actionList: string[] };

/** The class on every header and cell of the actions column, so a page can hide the column on a narrow screen. */
export const LIST_ACTIONS_CELL_CLASS = 'lt-actions-cell';

/** Gives each row every menu action, because the table draws the ⋮ trigger only for a row with a non-empty `actionList`. */
export function withActionList<T extends object>(
    rows: T[],
    menuItems: ReadonlyArray<{ action: string }>
): ActionListRow<T>[] {
    const actionList = menuItems.map((item) => item.action);
    return rows.map((row) => ({ ...row, actionList: [...actionList] }));
}

/** A ⋮ menu item, as far as the table reads it to decide whether a row shows it. */
export interface ListMenuItem<T> {
    label: string;
    action: string;
    hidden?: boolean | ((row: T) => boolean);
}

/** Whether the table hides `item` in the ⋮ menu of `row`, by the table's own rule. */
export function isMenuItemHidden<T>(item: ListMenuItem<T>, row: T & { actionList?: string[] }): boolean {
    if (typeof item.hidden === 'function') {
        return item.hidden(row);
    }
    if (typeof item.hidden === 'boolean') {
        return item.hidden;
    }
    return !row.actionList?.includes(item.action);
}

/**
 * The buttons `row` shows: each button whose menu item the row shows, so the buttons and the menu agree. A button with a
 * `disabledTitle` stays, disabled, where the menu hides its item, so it can say why the action cannot run.
 */
export function listActionButtons<T>(
    menuItems: ReadonlyArray<ListMenuItem<T>>,
    buttons: ReadonlyArray<ListActionButtonSpec>,
    row: T & { actionList?: string[] }
): ListActionButton[] {
    const result: ListActionButton[] = [];
    buttons.forEach((button) => {
        const item = menuItems.find((entry) => entry.action === button.action);
        if (!item) {
            return;
        }
        if (!isMenuItemHidden(item, row)) {
            result.push({ ...button, label: item.label });
        } else if (button.disabledTitle) {
            result.push({ ...button, label: item.label, disabled: () => true });
        }
    });
    return result;
}

/** How a page fills a row with action buttons. `buttons` is per row, so a button can follow that row's state. */
export interface ListRowActionsConfig<T> {
    menuItems: ReadonlyArray<ListMenuItem<T>>;
    buttons: (row: T) => ReadonlyArray<ListActionButtonSpec>;
    /** The prefix of each button id on the row. */
    cellId: (row: T) => string;
    run: (action: string, row: T) => void;
}

/** How a page fills the actions column with buttons. */
export interface ListActionButtonsConfig<T> extends ListRowActionsConfig<T> {
    /** About 30px for the cell padding, plus about 22px and 6px a character for each button, plus 8px between buttons. */
    width: number;
}

/** How a page fills a column with hover actions. `text` is what the cell shows, such as the row's name. */
export interface ListHoverActionsConfig<T> extends ListRowActionsConfig<T> {
    text: (row: T) => string;
    /** Markup in place of the text, such as a status with flags. The page escapes the values in it. */
    html?: (row: T) => string;
    /** A second line under the text, such as an id. */
    subtext?: (row: T) => string;
    /** A badge after the text, such as "Default", or null for none. */
    badge?: (row: T) => HoverCellBadge | null;
}

/**
 * The last column of a list, titled "Actions", with a row's quick-action buttons chosen by the ⋮ menu rules of
 * {@link listActionButtons}. A list with no buttons on its rows has no actions column.
 */
export function listActionButtonsColumn<T>(config: ListActionButtonsConfig<T>): ListColumn<T> {
    return {
        key: LIST_ACTIONS_COLUMN,
        label: 'Actions',
        width: config.width,
        valueFormatter: () => '',
        cell: ListActionsCellComponent,
        cellClass: LIST_ACTIONS_CELL_CLASS,
        cellInputs: (ctx: ListCellContext<T>) => ({
            buttons: listActionButtons(config.menuItems, config.buttons(ctx.row), ctx.row as T & { actionList?: string[] }),
            cellId: config.cellId(ctx.row),
            run: (action: string) => config.run(action, ctx.row),
        }),
        resizable: false,
        reorderable: false,
    };
}

/**
 * `column` drawn as a text cut with an ellipsis, with the row's quick actions as icons over its right end on hover. The
 * buttons follow the ⋮ menu rules of {@link listActionButtons}, so a list can drop its actions column.
 */
export function withHoverActions<T>(column: ListColumn<T>, config: ListHoverActionsConfig<T>): ListColumn<T> {
    return {
        ...column,
        cell: HoverActionsCellComponent,
        cellInputs: (ctx: ListCellContext<T>) => ({
            text: config.text(ctx.row),
            subtext: config.subtext?.(ctx.row) ?? '',
            html: config.html?.(ctx.row) ?? '',
            badge: config.badge?.(ctx.row) ?? null,
            buttons: listActionButtons(config.menuItems, config.buttons(ctx.row), ctx.row as T & { actionList?: string[] }),
            cellId: config.cellId(ctx.row),
            run: (action: string) => config.run(action, ctx.row),
        }),
    };
}

/**
 * Drops the widths the table saved for the columns a reader cannot resize, so the width in code always applies. The
 * table restores a saved width over the column's own, and a fixed column gives the reader no way to change it back.
 * The storage keys are the ones the table's ListTableService writes.
 */
export function forgetFixedColumnWidths(tableId: string, columns: ReadonlyArray<ListColumn<any>>): void {
    const fixed = new Set(columns.filter((column) => column.resizable === false).map((column) => column.key));
    if (!fixed.size) {
        return;
    }
    rewriteStored(`lt_${tableId}_column_widths`, (widths) => {
        if (widths && typeof widths === 'object' && !Array.isArray(widths)) {
            fixed.forEach((key) => delete widths[key]);
        }
        return widths;
    });
    rewriteStored(`lt_${tableId}_table_state`, (state) =>
        Array.isArray(state) ? state.map((entry) => (fixed.has(entry?.colId) ? { ...entry, width: undefined } : entry)) : state
    );
}

/**
 * Raises each saved width below its column's `minWidth` to that minimum. The table restores a saved width over the
 * column's own and reads `minWidth` only as a drag floor, so an older, narrower saved width would win otherwise.
 */
export function enforceMinColumnWidths(tableId: string, columns: ReadonlyArray<ListColumn<any>>): void {
    const mins = new Map(columns.filter((column) => column.minWidth != null).map((column) => [column.key, column.minWidth as number]));
    if (!mins.size) {
        return;
    }
    const raise = (key: string, width: unknown) => {
        const min = mins.get(key);
        return typeof width === 'number' && min != null && width < min ? min : width;
    };
    rewriteStored(`lt_${tableId}_column_widths`, (widths) => {
        if (widths && typeof widths === 'object' && !Array.isArray(widths)) {
            Object.keys(widths).forEach((key) => (widths[key] = raise(key, widths[key])));
        }
        return widths;
    });
    rewriteStored(`lt_${tableId}_table_state`, (state) =>
        Array.isArray(state) ? state.map((entry) => (entry?.width == null ? entry : { ...entry, width: raise(entry.colId, entry.width) })) : state
    );
}

/** Rewrites one stored JSON value. Storage can be missing, full or hold bad JSON, and none of that may break a page. */
function rewriteStored(key: string, rewrite: (value: any) => any): void {
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            localStorage.setItem(key, JSON.stringify(rewrite(JSON.parse(raw))));
        }
    } catch {
        // Nothing to fix, or nowhere to write it.
    }
}
